import { ReactElement, useCallback, useMemo } from 'react';
import { ColumnDescription } from 'react-bootstrap-table-next';
import { useSnackbar } from 'notistack';

import { deleteAdmingBillingListBilling } from '~services/admin/billingList/billing';
import { AdminBillingListBillingBase } from '~services/admin/billingList/billing/types';
import { DataTable, MenuActionsDataTable } from '~components/index';
import { formatHumanizeRange, formatCurrencyValue } from '~utils/formatter';
import { transformRangeDescriptionToText } from '~utils/logisiticsZones';
import { PATHS } from '~constants/paths';
import { useToggleDialogConfirmation, useLazyRequest } from '~hooks/index';
import { Nullable } from '~globals/types';
import {
  DialogBillingListBillingForm,
  DialogConfimation,
} from '~components/index';

import { ConfigBillingListBillingsTableProps } from './types';
import { ConfigBillingListBillingsTableContainer } from './styles';

const ConfigBillingListBillingsTable = ({
  loading,
  data,
  reloadData,
  billingListId,
  defaultBillingListCode,
}: ConfigBillingListBillingsTableProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const [
    openDialogUpdateBilling,
    selectedUpdateBillingId,
    handleOpenDialogUpdateBilling,
    handleCloseDialogUpdateBilling,
  ] = useToggleDialogConfirmation<Nullable<string>>(null);

  const onSuccessUpdateBilling = useCallback(() => {
    handleCloseDialogUpdateBilling();

    reloadData();
  }, [reloadData, handleCloseDialogUpdateBilling]);

  const [
    openConfirmDeleteBilling,
    selectedBillingToDelete,
    handleOpenConfirmDeleteBilling,
    handleCloseConfirmDeleteBilling,
  ] = useToggleDialogConfirmation<Nullable<AdminBillingListBillingBase>>(null);

  const messageDialogConfimationDeleteBilling = useMemo(
    () => `La tarifa ${selectedBillingToDelete?.billingCode} será eliminada`,
    [selectedBillingToDelete],
  );

  const handleSuccessDeleteBilling = useCallback(() => {
    handleCloseConfirmDeleteBilling();

    reloadData();
  }, [handleCloseConfirmDeleteBilling, reloadData]);

  const [, loadingDeleteBilling, , executeDeleteBilling] = useLazyRequest({
    request: deleteAdmingBillingListBilling,
    withPostSuccess: (response) => {
      const currentBillingListData = response.data?.data;

      // eslint-disable-next-line max-len
      const message = `La tarifa ${currentBillingListData?.billingCode} fue eliminada correctamente`;

      enqueueSnackbar(message, { variant: 'success' });

      handleSuccessDeleteBilling();
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo elimnar la tarifa, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const handleDeleteBilling = useCallback(() => {
    if (selectedBillingToDelete) {
      executeDeleteBilling({
        billingListId,
        billingId: selectedBillingToDelete.billingId,
      });
    }
  }, [billingListId, executeDeleteBilling, selectedBillingToDelete]);

  const columns = useMemo<ColumnDescription<AdminBillingListBillingBase>[]>(
    () => [
      {
        dataField: 'billingCode',
        text: 'Código',
        headerStyle: { width: 150 },
      },
      {
        dataField: 'billZipCodeRange',
        isDummyField: true,
        text: 'Rango de CPs',
        classes: 'not-truncated',
        formatter: (_cell, row) => {
          if (!row.billZipCodeRange) return 'N/A';

          return transformRangeDescriptionToText(
            row.billZipCodeRange.zipcodeRange,
          );
        },
      },
      {
        dataField: 'billingUnit1CapacityRange',
        isDummyField: true,
        text: 'Rango de carga primaria',
        classes: 'not-truncated',
        formatter: (_cell, row) => {
          if (!row.billingUnit1CapacityRange) return 'N/A';

          return formatHumanizeRange(
            row.billingUnit1CapacityRange.unitMinimumCapacity,
            row.billingUnit1CapacityRange.unitMaximumCapacity,
          );
        },
      },
      {
        dataField: 'billingUnit2CapacityRange',
        isDummyField: true,
        text: 'Rango de carga secundaria',
        classes: 'not-truncated',
        formatter: (_cell, row) => {
          if (!row.billingUnit2CapacityRange) return 'N/A';

          return formatHumanizeRange(
            row.billingUnit2CapacityRange.unitMinimumCapacity,
            row.billingUnit2CapacityRange.unitMaximumCapacity,
          );
        },
      },
      {
        dataField: 'billingPriceRange',
        isDummyField: true,
        text: 'Rango de precios',
        classes: 'not-truncated',
        formatter: (_cell, row) => {
          if (!row.billingPriceRange) return 'N/A';

          return formatHumanizeRange(
            row.billingPriceRange.minimumPrice,
            row.billingPriceRange.maximumPrice,
            (val) =>
              formatCurrencyValue(val, {
                style: 'currency',
                currency: 'USD',
                currencyDisplay: 'narrowSymbol',
                minimumFractionDigits: 2,
              }),
          );
        },
      },
      {
        dataField: 'billingByServiceType',
        isDummyField: true,
        text: 'Tipos de servicios',
        classes: 'not-truncated',
        formatter: (_cell, row) => {
          if (!row.billingByServiceType) return 'N/A';

          return transformRangeDescriptionToText(
            row.billingByServiceType.serviceTypes,
          );
        },
      },
      {
        dataField: 'shipPrice',
        text: 'Tarifa',
        formatter: (_cell, row) =>
          formatCurrencyValue(row.shipPrice, {
            style: 'currency',
            currency: 'USD',
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 2,
          }),
        headerStyle: { width: 110 },
        headerAlign: 'center',
        align: 'right',
      },
      {
        dataField: 'actions',
        isDummyField: true,
        text: '',
        formatter: (_cell, row) => (
          <MenuActionsDataTable
            resource={{
              path: PATHS.CONFIG.BILLING_LIST.BASE,
              id: row.billingListId,
            }}
            items={{
              show: { enabled: false },
              edit: {
                enabled: true,
                divider: true,
                onClick: () => handleOpenDialogUpdateBilling(row.billingId),
              },
              delete: {
                enabled: true,
                onClick: () => handleOpenConfirmDeleteBilling(row),
              },
            }}
          />
        ),
        headerStyle: { width: 60 },
      },
    ],
    [handleOpenDialogUpdateBilling, handleOpenConfirmDeleteBilling],
  );

  return (
    <ConfigBillingListBillingsTableContainer>
      <DataTable
        loading={loading}
        data={data}
        columns={columns}
        keyField="billingId"
      />

      {openDialogUpdateBilling && selectedUpdateBillingId && (
        <DialogBillingListBillingForm
          open={openDialogUpdateBilling}
          onClose={handleCloseDialogUpdateBilling}
          billingListId={billingListId}
          billingId={selectedUpdateBillingId}
          defaultBillingListCode={defaultBillingListCode}
          onSuccessSubmit={onSuccessUpdateBilling}
        />
      )}

      <DialogConfimation
        open={openConfirmDeleteBilling}
        description={messageDialogConfimationDeleteBilling}
        onConfirm={handleDeleteBilling}
        onCancel={() => handleCloseConfirmDeleteBilling()}
        loadingConfirm={loadingDeleteBilling}
      />
    </ConfigBillingListBillingsTableContainer>
  );
};

export default ConfigBillingListBillingsTable;
